import React, { Component } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './LoginPage.css';

// Import images
import cdcLogo from '../../../assets/icons/cdc-logo.png';
import { ArrowLeftBlack } from '../../../assets/icons';
import AlertBox from '../../../components/AlertBox/AlertBox';

// Import Redux actions
import LoginActions from '../../../redux/LoginRedux';
import { LoginSelectors } from '../../../redux/LoginRedux';

// TODO: Import navigation utilities when ready
// import { useNavigate } from 'react-router-dom';

// Web Push Notifications
const requestNotificationPermission = async (): Promise<string | null> => {
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return null;
  }

  if (Notification.permission === 'granted') {
    console.log('Notification permission already granted');
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    console.log('Notification permission denied');
    return null;
  }

  const permission = await Notification.requestPermission();
  if (permission === 'granted') {
    console.log('Notification permission granted');
    return 'granted';
  }

  return null;
};

const getNotificationToken = async (): Promise<string | null> => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.log('Push messaging is not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: 'YOUR_VAPID_PUBLIC_KEY' // TODO: Replace with actual VAPID key
    });
    
    const key = subscription.getKey('p256dh');
    if (!key) return null;
    const token = btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(key))));
    console.log('Push notification token:', token);
    return token;
  } catch (error) {
    console.error('Error getting push notification token:', error);
    return null;
  }
};

// Localization Detection
const detectLocale = (): string => {
  const browserLang = navigator.language || navigator.languages?.[0] || 'en';
  const locale = localStorage.getItem('i18nextLng') || browserLang.slice(0, 2);
  
  switch (locale) {
    case 'en':
      return 'en';
    case 'zh':
      return 'hk';
    default:
      return 'en';
  }
};

// Back Arrow Icon Component
const BackArrowIcon: React.FC = () => (
  <ArrowLeftBlack />
);

// CDC Logo Component
const CDCLogo: React.FC = () => (
  <div className="cdc-logo">
    <img 
      src={cdcLogo} 
      alt="CDC Logo" 
      className="cdc-logo-image"
    />
  </div>
);

interface LoginPageState {
  email: string;
  password: string;
  notificationToken: string | null;
  locale: string;
  loading: boolean;
  emailError: string | null;
  passwordError: string | null;
  props?: LoginPageProps; // Add props for getDerivedStateFromProps
  
  alertTitle: string;
  alertMessage: string;
  alertPopupOpen: boolean;
}

interface LoginPageProps extends WithTranslation {
  // Redux props
  fetching?: boolean;
  success?: boolean;
  error?: string | null;
  login?: (email: string, password: string, notificationToken: string, locale: string) => void;
  resetData?: () => void;
  // Navigation props
  navigate?: (path: string) => void;
  goBack?: () => void;
}

class LoginPage extends Component<LoginPageProps, LoginPageState> {
  constructor(props: LoginPageProps) {
    super(props);
    this.state = {
      email: '',
      password: '',
      notificationToken: null,
      locale: 'en',
      loading: false,
      emailError: null,
      passwordError: null,
      alertTitle: '',
      alertMessage: '',
      alertPopupOpen: false,
    };
  }

  // Implement notification permission request
  async askPermission() {
    const permission = await requestNotificationPermission();
    if (permission === 'granted') {
      const token = await getNotificationToken();
      this.setState({
        notificationToken: token,
      });
    }
  }

  // Implement componentDidMount with localization and notifications
  async componentDidMount() {
    // Reset login data on component mount
    if (this.props.resetData) {
      this.props.resetData();
    }
    
    const locale = detectLocale();
    this.setState({
      locale: locale,
    });
    await this.askPermission();
  }

  // Handle error display
  componentDidUpdate(prevProps: LoginPageProps) {
    if (this.props.error && this.props.error !== prevProps.error) {
      // this.showErrorAlert(this.props.t('login-screen:alert.fail.message'));
      this.setState({
        alertTitle: this.props.t('login-screen:alert.fail.title'),
        alertMessage: this.props.t('login-screen:alert.fail.message'),
        alertPopupOpen: true,
      });
    }
  }

  // Cleanup on unmount
  componentWillUnmount() {
    // Cleanup any event listeners if needed
    // Note: Web doesn't need BackHandler cleanup like React Native
  }

  // Implement back navigation
  back() {
    if (this.props.goBack) {
      this.props.goBack();
    } else {
      // Fallback to browser back
      window.history.back();
    }
  }

  // Implement login functionality
  _onLoginPress() {
    if (this.props.login) {
      this.props.login(
        this.state.email,
        this.state.password,
        this.state.notificationToken || 'no-token',
        this.state.locale
      );
    }
  }

  // Implement forgot password navigation
  _onForgotPasswordPress() {
    if (this.props.navigate) {
      this.props.navigate('/forgot-password');
    } else {
      // Fallback to window location
      window.location.href = '/forgot-password';
    }
  }

  // Implement terms and conditions navigation
  _onTermsAndConditionPress() {
    if (this.props.navigate) {
      this.props.navigate('/terms-and-conditions');
    } else {
      // Fallback to window location
      window.location.href = '/terms-and-conditions';
    }
  }

  // Implement browser back button handling
  handleAndroidBackPress = () => {
    // Handle browser back button
    this.back();
    return true;
  }

  // Custom error alert
  /* showErrorAlert = (message: string) => {
    // Create a custom alert element
    const alertDiv = document.createElement('div');
    alertDiv.className = 'custom-alert';
    alertDiv.innerHTML = `
      <div class="alert-content">
        <div class="alert-message">${message}</div>
        <button class="alert-button" onclick="this.parentElement.parentElement.remove()">OK</button>
      </div>
    `;
    
    // Add styles
    alertDiv.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    `;
    
    // Add to body
    document.body.appendChild(alertDiv);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      if (alertDiv.parentElement) {
        alertDiv.remove();
      }
    }, 5000);
  } */

  alertBoxPopupOk = () => {
    this.setState({
      alertPopupOpen: false,
    });
  }
  
  alertBoxPopupClose = () => {
    this.setState({
      alertPopupOpen: false,
    });
  }

  // Implement getDerivedStateFromProps for Redux state handling
  static getDerivedStateFromProps(nextProps: LoginPageProps, prevState: LoginPageState) {
    if (prevState.props) {
      if (!nextProps.fetching) {
        if (
          nextProps.success &&
          nextProps.success !== prevState.props.success
        ) {
          // Navigate to auth loading screen
          if (nextProps.navigate) {
            // nextProps.navigate('/auth-loading');
            nextProps.navigate('/goals');
          } else {
            // Fallback to window location
            // window.location.href = '/auth-loading';
            window.location.href = '/goals';
          }
          console.log('Login successful!');
          return {
            email: '',
            password: '',
          };
        }

        if (nextProps.error && nextProps.error !== prevState.props.error) {
          // Show custom error alert - we'll use a simple alert for now
          // TODO: Implement proper error handling in componentDidUpdate
          console.error('Login error:', nextProps.error);
        }
      }
    }

    return {
      props: nextProps,
    };
  }

  handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ email: e.target.value });
    // For development testing (uncomment to auto-fill):
    // this.setState({ email: 'tony.luu@rh-studio.net' });
  };

  handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ password: e.target.value });
    // For development testing (uncomment to auto-fill):
    // this.setState({ password: 'travis123' });
  };

  // Form validation
  validateEmail = (email: string): string | null => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return 'Email is required';
    }
    /* if (email && !emailRegex.test(email)) {
      return 'Please enter a valid email address';
    } */
    return null;
  };

  validatePassword = (password: string): string | null => {
    if (!password) {
      return 'Password is required';
    }
    /* if (password.length < 6) {
      return 'Password must be at least 6 characters';
    } */
    return null;
  };

  handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const emailError = this.validateEmail(this.state.email);
    const passwordError = this.validatePassword(this.state.password);

    if ( emailError || passwordError ) {
      // this.showErrorAlert(this.props.t('login-screen:alert.input-missing.message'));
      this.setState({
        alertTitle: this.props.t('login-screen:alert.input-missing.title'),
        alertMessage: this.props.t('login-screen:alert.input-missing.message'),
        alertPopupOpen: true,
      });
      return;
    }
    
    this.setState({
      emailError,
      passwordError,
    });
    
    // If no errors, proceed with login
    if (!emailError && !passwordError) {
      this._onLoginPress();
    }
  };

  handleBackClick = () => {
    this.back();
  };

  handleForgotPasswordClick = () => {
    this._onForgotPasswordPress();
  };

  handleTermsClick = () => {
    this._onTermsAndConditionPress();
  };

  render() {
    const { email, password, emailError, passwordError } = this.state;
    const { t, fetching, success, error } = this.props;

    return (
      <div className="login-page">
        {/* Header */}
        <header className="login-header">
          <div className="header-left">
            <button 
              className="back-button"
              onClick={this.handleBackClick}
              type="button"
            >
              <BackArrowIcon />
            </button>
          </div>
          <div className="header-body"></div>
          <div className="header-right"></div>
        </header>

        {/* Main Content - Matching ScrollView structure */}
        <div className="login-body">
          <div className="login-container">
          {/* Logo Section */}
          <div className="logo-container">
            <CDCLogo />
          </div>

          {/* Form Section - Matching native-base Item structure */}
          <form className="form-container" onSubmit={this.handleSubmit}>
            <div className="form-container">
              {/* Email Input */}
              <div className="form-group">
                <label className="form-label uppercase">
                  {t('login-screen:form.label.email')}
                </label>
                <div className="input-item">
                  <input
                    type="email"
                    className={`form-input`}
                    placeholder={t('login-screen:form.placeholder.email')}
                    value={email}
                    onChange={this.handleEmailChange}
                    autoCapitalize="none"                    
                  />
                </div>
              </div>

              {/* Password Input */}
              <div className="form-group">
                <label className="form-label uppercase">
                  {t('login-screen:form.label.password')}
                </label>
                <div className="input-item">
                  <input
                    type="password"
                    className={`form-input`}
                    placeholder={t('login-screen:form.placeholder.password')}
                    value={password}
                    onChange={this.handlePasswordChange}
                    autoCapitalize="none"                    
                  />
                </div>
              </div>

              {/* Forgot Password Link */}
              <div className="forgot-password-container">
                <button
                  type="button"
                  className="forgot-password-link"
                  onClick={this.handleForgotPasswordClick}
                >
                  {t('login-screen:forgot-password')}
                </button>
              </div>
            </div>

          {/* Terms and Conditions Section */}
          <div className="terms-container">
            <span className="terms-text">
              {t('login-screen:terms-and-condition-text')}
            </span>
            <button
              type="button"
              className="terms-link"
              onClick={this.handleTermsClick}
            >
              {t('login-screen:terms-and-condition-button')}
            </button>
          </div>

          {/* Login Button */}
          <div className="button-container">
            <button
              type="submit"
              className="login-button comman-btn w-100"
              disabled={fetching}
            >
              <span className="button-text uppercase">
                {fetching ? t('login-screen:button.signing-in') : t('login-screen:button.login')}
              </span>
            </button>
          </div>
        </form>

          </div>

          <AlertBox
            title={this.state.alertTitle}
            message={this.state.alertMessage}
            okAction={this.alertBoxPopupOk}
            isVisible={this.state.alertPopupOpen ? true : false}
            onClose={() => {}}
          />

        </div>
      </div>
    );
  }
}

// Add Redux connection
const mapStateToProps = (state: any) => {
  return {
    fetching: LoginSelectors.isFetching(state),
    success: LoginSelectors.isSuccess(state),
    error: LoginSelectors.hasError(state),
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    login: (email: string, password: string, notificationToken: string, locale: string) =>
      dispatch(
        LoginActions.loginRequest(email, password, notificationToken, locale)
      ),
    resetData: () => dispatch(LoginActions.resetData()),
  };
};

// Wrapper component to provide navigation props
const LoginPageWithNavigation: React.FC<WithTranslation> = (props) => {
  const navigate = useNavigate();
  
  const handleNavigate = (path: string) => {
    navigate(path);
  };
  
  const handleGoBack = () => {
    navigate(-1);
  };
  
  return (
    <LoginPage 
      {...props}
      navigate={handleNavigate}
      goBack={handleGoBack}
    />
  );
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(LoginPageWithNavigation));
